// Copyright 2026, Auxilio Technologies LLC

import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { alertsAPI } from '../services/api';
import {
  BellIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  XMarkIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  TruckIcon,
  BuildingStorefrontIcon,
  CubeIcon,
  ArrowPathIcon,
  EyeIcon,
  TrashIcon,
  CheckIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  BarsArrowDownIcon,
  ArrowTopRightOnSquareIcon,
  Cog6ToothIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  ComputerDesktopIcon,
} from '@heroicons/react/24/outline';
import { BellAlertIcon } from '@heroicons/react/24/solid';
import AlertNotificationRules from '../components/AlertNotificationRules';

type SortField = 'timestamp' | 'severity' | 'status' | 'title' | 'category';
type SortDirection = 'asc' | 'desc';
type CardFilter = 'all' | 'active' | 'critical' | 'acknowledged' | 'resolved' | 'actionRequired';

type AlertSeverity = 'critical' | 'high' | 'medium' | 'low' | 'info';
type AlertStatus = 'active' | 'acknowledged' | 'resolved' | 'dismissed';
type AlertCategory = 'shipment' | 'capacity' | 'delay' | 'inventory' | 'system' | 'compliance';

interface Alert {
  id: string;
  title: string;
  message: string;
  severity: AlertSeverity;
  status: AlertStatus;
  category: AlertCategory;
  source: string;
  timestamp: string;
  acknowledgedBy?: string;
  acknowledgedAt?: string;
  resolvedAt?: string;
  relatedEntity?: string;
  relatedEntityId?: string;
  actionRequired: boolean;
  autoResolve: boolean;
}

interface NotificationChannel {
  enabled: boolean;
  severities: AlertSeverity[];
}

interface AlertConfig {
  notifications: {
    inApp: NotificationChannel;
    email: NotificationChannel;
    sms: NotificationChannel;
    push: NotificationChannel;
  };
  categories: {
    [key in AlertCategory]: boolean;
  };
  quietHours: {
    enabled: boolean;
    start: string;
    end: string;
    excludeCritical: boolean;
  };
  autoAcknowledge: {
    enabled: boolean;
    afterMinutes: number;
  };
}

const defaultAlertConfig: AlertConfig = {
  notifications: {
    inApp: { enabled: true, severities: ['critical', 'high', 'medium', 'low', 'info'] },
    email: { enabled: true, severities: ['critical', 'high'] },
    sms: { enabled: false, severities: ['critical'] },
    push: { enabled: true, severities: ['critical', 'high', 'medium'] },
  },
  categories: {
    shipment: true,
    capacity: true,
    delay: true,
    inventory: true,
    system: true,
    compliance: true,
  },
  quietHours: {
    enabled: false,
    start: '22:00',
    end: '07:00',
    excludeCritical: true,
  },
  autoAcknowledge: {
    enabled: false,
    afterMinutes: 30,
  },
};

const mockAlerts: Alert[] = [
  {
    id: 'ALT-001',
    title: 'Critical Delay: Shipment SHP-002',
    message: 'Shipment from Port-LA to DC-Phoenix is delayed by 5+ hours due to traffic incident on I-10. Temperature-sensitive cargo at risk.',
    severity: 'critical',
    status: 'active',
    category: 'delay',
    source: 'Tracking System',
    timestamp: '2024-01-10 09:15:00',
    relatedEntity: 'Shipment',
    relatedEntityId: 'SHP-002',
    actionRequired: true,
    autoResolve: false,
  },
  {
    id: 'ALT-002',
    title: 'Capacity Warning: DC-Chicago',
    message: 'Distribution Center Chicago is at 94% capacity. Incoming shipments may need to be rerouted.',
    severity: 'high',
    status: 'acknowledged',
    category: 'capacity',
    source: 'Warehouse Management',
    timestamp: '2024-01-10 08:30:00',
    acknowledgedBy: 'John Smith',
    acknowledgedAt: '2024-01-10 08:45:00',
    relatedEntity: 'Facility',
    relatedEntityId: 'DC-CHI-01',
    actionRequired: true,
    autoResolve: false,
  },
  {
    id: 'ALT-003',
    title: 'Low Inventory: SKU-78542',
    message: 'Product "Industrial Lubricant 5L" is below safety stock level at WH-Detroit. Current: 45 units, Safety Stock: 100 units.',
    severity: 'medium',
    status: 'active',
    category: 'inventory',
    source: 'Inventory Management',
    timestamp: '2024-01-10 07:00:00',
    relatedEntity: 'Product',
    relatedEntityId: 'SKU-78542',
    actionRequired: true,
    autoResolve: false,
  },
  {
    id: 'ALT-004',
    title: 'Route Efficiency Below Target',
    message: 'Route RT-005 (Great Lakes Circuit) efficiency dropped to 71%, below the 80% threshold. Consider re-optimization.',
    severity: 'medium',
    status: 'active',
    category: 'shipment',
    source: 'Route Optimizer',
    timestamp: '2024-01-10 06:45:00',
    relatedEntity: 'Route',
    relatedEntityId: 'RT-005',
    actionRequired: false,
    autoResolve: true,
  },
  {
    id: 'ALT-005',
    title: 'Compliance: Driver Hours Exceeded',
    message: 'Driver Tom Anderson (ID: DRV-0042) has exceeded maximum driving hours. Mandatory rest period required.',
    severity: 'critical',
    status: 'acknowledged',
    category: 'compliance',
    source: 'Fleet Management',
    timestamp: '2024-01-10 05:30:00',
    acknowledgedBy: 'Sarah Johnson',
    acknowledgedAt: '2024-01-10 05:35:00',
    relatedEntity: 'Driver',
    relatedEntityId: 'DRV-0042',
    actionRequired: true,
    autoResolve: false,
  },
  {
    id: 'ALT-006',
    title: 'System: API Rate Limit Warning',
    message: 'Carrier API (Swift Logistics) approaching rate limit. 85% of daily quota used.',
    severity: 'low',
    status: 'active',
    category: 'system',
    source: 'Integration Monitor',
    timestamp: '2024-01-10 10:00:00',
    actionRequired: false,
    autoResolve: true,
  },
  {
    id: 'ALT-007',
    title: 'Shipment Delivered Early',
    message: 'Shipment SHP-003 delivered 28 minutes ahead of schedule to Store-Miami-12.',
    severity: 'info',
    status: 'resolved',
    category: 'shipment',
    source: 'Tracking System',
    timestamp: '2024-01-09 17:32:00',
    resolvedAt: '2024-01-09 17:32:00',
    relatedEntity: 'Shipment',
    relatedEntityId: 'SHP-003',
    actionRequired: false,
    autoResolve: true,
  },
  {
    id: 'ALT-008',
    title: 'Weather Advisory: Midwest Region',
    message: 'Winter storm warning for IL, WI, IN. Expect delays on routes through these areas over the next 24 hours.',
    severity: 'high',
    status: 'active',
    category: 'delay',
    source: 'Weather Service',
    timestamp: '2024-01-10 04:00:00',
    actionRequired: false,
    autoResolve: true,
  },
  {
    id: 'ALT-009',
    title: 'Capacity Normalized: WH-Dallas',
    message: 'Warehouse Dallas capacity returned to normal levels (72%) after outbound shipments.',
    severity: 'info',
    status: 'resolved',
    category: 'capacity',
    source: 'Warehouse Management',
    timestamp: '2024-01-09 16:00:00',
    resolvedAt: '2024-01-09 16:00:00',
    relatedEntity: 'Facility',
    relatedEntityId: 'WH-DAL-01',
    actionRequired: false,
    autoResolve: true,
  },
  {
    id: 'ALT-010',
    title: 'Delivery Exception: Access Denied',
    message: 'Delivery attempt failed at Store-Milwaukee-5. Gate code invalid. Driver waiting for assistance.',
    severity: 'high',
    status: 'active',
    category: 'shipment',
    source: 'Driver App',
    timestamp: '2024-01-10 10:15:00',
    relatedEntity: 'Shipment',
    relatedEntityId: 'SHP-007',
    actionRequired: true,
    autoResolve: false,
  },
  {
    id: 'ALT-011',
    title: 'Temperature Deviation: Reefer Unit',
    message: 'Reefer unit on truck TRK-0089 showing temperature spike. Current: 42°F, Target: 35°F.',
    severity: 'critical',
    status: 'active',
    category: 'shipment',
    source: 'IoT Sensors',
    timestamp: '2024-01-10 10:20:00',
    relatedEntity: 'Vehicle',
    relatedEntityId: 'TRK-0089',
    actionRequired: true,
    autoResolve: false,
  },
  {
    id: 'ALT-012',
    title: 'Scheduled Maintenance: Server Update',
    message: 'System maintenance scheduled for tonight 2:00 AM - 4:00 AM EST. Some features may be unavailable.',
    severity: 'info',
    status: 'active',
    category: 'system',
    source: 'IT Operations',
    timestamp: '2024-01-10 09:00:00',
    actionRequired: false,
    autoResolve: true,
  },
];

// Severity order for sorting (critical first)
const severityOrder: Record<AlertSeverity, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
  info: 4,
};

// Status order for sorting
const statusOrder: Record<AlertStatus, number> = {
  active: 0,
  acknowledged: 1,
  resolved: 2,
  dismissed: 3,
};

// Get unique sources for filter dropdown
const getUniqueSources = (alerts: Alert[]) => [...new Set(alerts.map(a => a.source))];

const Alerts: React.FC = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<Alert[]>(mockAlerts);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);

  // Fetch alerts from API on mount
  useEffect(() => {
    const fetchAlerts = async () => {
      setLoading(true);
      try {
        const response = await alertsAPI.list();
        if (response.data && Array.isArray(response.data)) {
          setAlerts(response.data);
        }
      } catch (error) {
        console.error('Error fetching alerts:', error);
        // Keep mock data as fallback
      } finally {
        setLoading(false);
      }
    };
    fetchAlerts();
  }, []);

  // Configuration modal state
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [alertConfig, setAlertConfig] = useState<AlertConfig>(defaultAlertConfig);
  const [configTab, setConfigTab] = useState<'notifications' | 'categories' | 'schedule' | 'routing'>('notifications');

  // Card filter state (for clickable stats cards)
  const [cardFilter, setCardFilter] = useState<CardFilter>('all');

  // Navigate to entity based on type
  const navigateToEntity = (entityType: string | undefined, entityId: string | undefined) => {
    if (!entityType || !entityId) return;

    // Map entity types to their corresponding routes and query params
    const entityRouteMap: Record<string, { route: string; param: string }> = {
      'Shipment': { route: '/shipments', param: 'shipment' },
      'Facility': { route: '/sites', param: 'site' },
      'Route': { route: '/routes', param: 'route' },
      'Vehicle': { route: '/control-tower', param: 'vehicle' },
      'Driver': { route: '/control-tower', param: 'driver' },
      'Product': { route: '/forecasting', param: 'product' },
    };

    const mapping = entityRouteMap[entityType];
    if (mapping) {
      navigate(`${mapping.route}?${mapping.param}=${encodeURIComponent(entityId)}`);
    }
  };

  // More filters state
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [autoResolveFilter, setAutoResolveFilter] = useState<string>('');
  const [dateRangeStart, setDateRangeStart] = useState<string>('');
  const [dateRangeEnd, setDateRangeEnd] = useState<string>('');

  // Sort state
  const [sortField, setSortField] = useState<SortField>('timestamp');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Handle card filter click
  const handleCardFilterClick = (filter: CardFilter) => {
    setCardFilter(filter);
    // Reset dropdown filters when using card filters
    if (filter !== 'all') {
      setSeverityFilter('');
      setStatusFilter('');
    }
  };

  // Apply card-based filtering
  const applyCardFilter = (alert: Alert): boolean => {
    switch (cardFilter) {
      case 'active':
        return alert.status === 'active';
      case 'critical':
        return alert.severity === 'critical' && alert.status === 'active';
      case 'acknowledged':
        return alert.status === 'acknowledged';
      case 'resolved':
        return alert.status === 'resolved';
      case 'actionRequired':
        return alert.actionRequired && alert.status === 'active';
      case 'all':
      default:
        return true;
    }
  };

  const filteredAlerts = useMemo(() => {
    let result = alerts.filter((alert) => {
      const matchesSearch =
        alert.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        alert.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        alert.id.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesSeverity = !severityFilter || alert.severity === severityFilter;
      const matchesStatus = !statusFilter || alert.status === statusFilter;
      const matchesCategory = !categoryFilter || alert.category === categoryFilter;
      const matchesCardFilter = applyCardFilter(alert);
      const matchesSource = !sourceFilter || alert.source === sourceFilter;
      const matchesAutoResolve = !autoResolveFilter ||
        (autoResolveFilter === 'yes' ? alert.autoResolve : !alert.autoResolve);

      // Date range filtering
      let matchesDateRange = true;
      if (dateRangeStart || dateRangeEnd) {
        const alertDate = new Date(alert.timestamp);
        if (dateRangeStart) {
          matchesDateRange = matchesDateRange && alertDate >= new Date(dateRangeStart);
        }
        if (dateRangeEnd) {
          const endDate = new Date(dateRangeEnd);
          endDate.setHours(23, 59, 59, 999);
          matchesDateRange = matchesDateRange && alertDate <= endDate;
        }
      }

      return matchesSearch && matchesSeverity && matchesStatus && matchesCategory &&
             matchesCardFilter && matchesSource && matchesAutoResolve && matchesDateRange;
    });

    // Apply sorting
    result.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'timestamp':
          comparison = new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
          break;
        case 'severity':
          comparison = severityOrder[a.severity] - severityOrder[b.severity];
          break;
        case 'status':
          comparison = statusOrder[a.status] - statusOrder[b.status];
          break;
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'category':
          comparison = a.category.localeCompare(b.category);
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [alerts, searchTerm, severityFilter, statusFilter, categoryFilter, cardFilter,
      sourceFilter, autoResolveFilter, dateRangeStart, dateRangeEnd, sortField, sortDirection]);

  const handleSortChange = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const hasActiveMoreFilters = sourceFilter || autoResolveFilter || dateRangeStart || dateRangeEnd;

  const handleAcknowledge = async (alertId: string) => {
    try {
      await alertsAPI.acknowledge(alertId);
      // Update local state on success
      setAlerts((prev) =>
        prev.map((alert) =>
          alert.id === alertId
            ? {
                ...alert,
                status: 'acknowledged' as AlertStatus,
                acknowledgedBy: 'Current User',
                acknowledgedAt: new Date().toISOString(),
              }
            : alert
        )
      );
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      // Optionally show error toast
    }
  };

  const handleResolve = async (alertId: string) => {
    try {
      await alertsAPI.resolve(alertId, 'Resolved via UI');
      // Update local state on success
      setAlerts((prev) =>
        prev.map((alert) =>
          alert.id === alertId
            ? {
                ...alert,
                status: 'resolved' as AlertStatus,
                resolvedAt: new Date().toISOString(),
              }
            : alert
        )
      );
    } catch (error) {
      console.error('Error resolving alert:', error);
      // Optionally show error toast
    }
  };

  const handleDismiss = (alertId: string) => {
    setAlerts((prev) =>
      prev.map((alert) =>
        alert.id === alertId ? { ...alert, status: 'dismissed' as AlertStatus } : alert
      )
    );
  };

  // Alert configuration helpers
  const toggleNotificationChannel = (channel: keyof AlertConfig['notifications']) => {
    setAlertConfig(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [channel]: {
          ...prev.notifications[channel],
          enabled: !prev.notifications[channel].enabled
        }
      }
    }));
  };

  const toggleNotificationSeverity = (channel: keyof AlertConfig['notifications'], severity: AlertSeverity) => {
    setAlertConfig(prev => {
      const currentSeverities = prev.notifications[channel].severities;
      const newSeverities = currentSeverities.includes(severity)
        ? currentSeverities.filter(s => s !== severity)
        : [...currentSeverities, severity];
      return {
        ...prev,
        notifications: {
          ...prev.notifications,
          [channel]: {
            ...prev.notifications[channel],
            severities: newSeverities
          }
        }
      };
    });
  };

  const toggleCategory = (category: AlertCategory) => {
    setAlertConfig(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [category]: !prev.categories[category]
      }
    }));
  };

  const handleSaveConfig = () => {
    // In production, this would save to backend/localStorage
    console.log('Saving alert configuration:', alertConfig);
    setShowConfigModal(false);
  };

  const getSeverityIcon = (severity: AlertSeverity) => {
    const iconClass = 'w-5 h-5';
    switch (severity) {
      case 'critical':
        return <ExclamationCircleIcon className={`${iconClass} text-red-500`} />;
      case 'high':
        return <ExclamationTriangleIcon className={`${iconClass} text-orange-500`} />;
      case 'medium':
        return <ExclamationTriangleIcon className={`${iconClass} text-yellow-500`} />;
      case 'low':
        return <InformationCircleIcon className={`${iconClass} text-blue-500`} />;
      case 'info':
        return <InformationCircleIcon className={`${iconClass} text-gray-500`} />;
    }
  };

  const getSeverityBadge = (severity: AlertSeverity) => {
    const styles = {
      critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
      medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      low: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      info: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${styles[severity]}`}>
        {severity}
      </span>
    );
  };

  const getStatusBadge = (status: AlertStatus) => {
    const styles = {
      active: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      acknowledged: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      resolved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      dismissed: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-500',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${styles[status]}`}>
        {status}
      </span>
    );
  };

  const getCategoryIcon = (category: AlertCategory) => {
    const iconClass = 'w-4 h-4';
    switch (category) {
      case 'shipment':
        return <TruckIcon className={iconClass} />;
      case 'capacity':
        return <BuildingStorefrontIcon className={iconClass} />;
      case 'delay':
        return <ClockIcon className={iconClass} />;
      case 'inventory':
        return <CubeIcon className={iconClass} />;
      case 'system':
        return <ArrowPathIcon className={iconClass} />;
      case 'compliance':
        return <ExclamationTriangleIcon className={iconClass} />;
    }
  };

  const stats = {
    total: alerts.length,
    active: alerts.filter((a) => a.status === 'active').length,
    critical: alerts.filter((a) => a.severity === 'critical' && a.status === 'active').length,
    acknowledged: alerts.filter((a) => a.status === 'acknowledged').length,
    resolved: alerts.filter((a) => a.status === 'resolved').length,
    actionRequired: alerts.filter((a) => a.actionRequired && a.status === 'active').length,
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Alerts</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Monitor and manage operational alerts across your network
          </p>
        </div>
        <div className="flex gap-2">
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <CheckIcon className="w-4 h-4 mr-2" />
            Mark All Read
          </button>
          <button
            onClick={() => setShowConfigModal(true)}
            className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors"
          >
            <Cog6ToothIcon className="w-4 h-4 mr-2" />
            Configure Alerts
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <button
          onClick={() => handleCardFilterClick('all')}
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-4 text-left transition-all hover:shadow-md ${
            cardFilter === 'all'
              ? 'border-teal-500 ring-2 ring-teal-500'
              : 'border-gray-100 dark:border-gray-700'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Alerts</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
            </div>
            <BellIcon className="w-8 h-8 text-gray-400" />
          </div>
        </button>
        <button
          onClick={() => handleCardFilterClick('active')}
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-4 text-left transition-all hover:shadow-md ${
            cardFilter === 'active'
              ? 'border-red-500 ring-2 ring-red-500'
              : 'border-gray-100 dark:border-gray-700'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Active</p>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.active}</p>
            </div>
            <BellAlertIcon className="w-8 h-8 text-red-500" />
          </div>
        </button>
        <button
          onClick={() => handleCardFilterClick('critical')}
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-4 text-left transition-all hover:shadow-md ${
            cardFilter === 'critical'
              ? 'border-red-500 ring-2 ring-red-500'
              : 'border-gray-100 dark:border-gray-700'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Critical</p>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.critical}</p>
            </div>
            <ExclamationCircleIcon className="w-8 h-8 text-red-500" />
          </div>
        </button>
        <button
          onClick={() => handleCardFilterClick('acknowledged')}
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-4 text-left transition-all hover:shadow-md ${
            cardFilter === 'acknowledged'
              ? 'border-yellow-500 ring-2 ring-yellow-500'
              : 'border-gray-100 dark:border-gray-700'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Acknowledged</p>
              <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats.acknowledged}</p>
            </div>
            <CheckCircleIcon className="w-8 h-8 text-yellow-500" />
          </div>
        </button>
        <button
          onClick={() => handleCardFilterClick('resolved')}
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-4 text-left transition-all hover:shadow-md ${
            cardFilter === 'resolved'
              ? 'border-green-500 ring-2 ring-green-500'
              : 'border-gray-100 dark:border-gray-700'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Resolved</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.resolved}</p>
            </div>
            <CheckCircleIcon className="w-8 h-8 text-green-500" />
          </div>
        </button>
        <button
          onClick={() => handleCardFilterClick('actionRequired')}
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-4 text-left transition-all hover:shadow-md ${
            cardFilter === 'actionRequired'
              ? 'border-orange-500 ring-2 ring-orange-500'
              : 'border-gray-100 dark:border-gray-700'
          }`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Action Required</p>
              <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{stats.actionRequired}</p>
            </div>
            <ExclamationTriangleIcon className="w-8 h-8 text-orange-500" />
          </div>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search alerts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
            >
              <option value="">All Severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
              <option value="info">Info</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="resolved">Resolved</option>
              <option value="dismissed">Dismissed</option>
            </select>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
            >
              <option value="">All Categories</option>
              <option value="shipment">Shipment</option>
              <option value="capacity">Capacity</option>
              <option value="delay">Delay</option>
              <option value="inventory">Inventory</option>
              <option value="system">System</option>
              <option value="compliance">Compliance</option>
            </select>
            <button
              onClick={() => setShowMoreFilters(!showMoreFilters)}
              className={`inline-flex items-center px-4 py-2 border rounded-lg transition-colors ${
                showMoreFilters || hasActiveMoreFilters
                  ? 'border-teal-500 bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 dark:border-teal-500'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <FunnelIcon className="w-4 h-4 mr-2" />
              More Filters
              {hasActiveMoreFilters && (
                <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-teal-600 rounded-full">
                  {[sourceFilter, autoResolveFilter, dateRangeStart, dateRangeEnd].filter(Boolean).length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* More Filters Panel */}
        {showMoreFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Source
                </label>
                <select
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">All Sources</option>
                  {getUniqueSources(alerts).map((source) => (
                    <option key={source} value={source}>{source}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Auto-Resolve
                </label>
                <select
                  value={autoResolveFilter}
                  onChange={(e) => setAutoResolveFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">All</option>
                  <option value="yes">Auto-Resolve Enabled</option>
                  <option value="no">Manual Resolution Required</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date Range
                </label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={dateRangeStart}
                    onChange={(e) => setDateRangeStart(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                  />
                  <input
                    type="date"
                    value={dateRangeEnd}
                    onChange={(e) => setDateRangeEnd(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSourceFilter('');
                    setAutoResolveFilter('');
                    setDateRangeStart('');
                    setDateRangeEnd('');
                  }}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sort Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <BarsArrowDownIcon className="w-4 h-4" />
            <span>Sort by:</span>
            <div className="flex flex-wrap gap-2">
              {[
                { field: 'timestamp' as SortField, label: 'Time' },
                { field: 'severity' as SortField, label: 'Severity' },
                { field: 'status' as SortField, label: 'Status' },
                { field: 'category' as SortField, label: 'Category' },
                { field: 'title' as SortField, label: 'Title' },
              ].map(({ field, label }) => (
                <button
                  key={field}
                  onClick={() => handleSortChange(field)}
                  className={`inline-flex items-center px-3 py-1 rounded-lg text-sm transition-colors ${
                    sortField === field
                      ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {label}
                  {sortField === field && (
                    sortDirection === 'desc'
                      ? <ChevronDownIcon className="w-3 h-3 ml-1" />
                      : <ChevronUpIcon className="w-3 h-3 ml-1" />
                  )}
                </button>
              ))}
            </div>
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Showing {filteredAlerts.length} of {alerts.length} alerts
          </div>
        </div>
      </div>

      {/* Alerts List */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {filteredAlerts.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              No alerts match your filters
            </div>
          ) : (
            filteredAlerts.map((alert) => (
              <div
                key={alert.id}
                className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors ${
                  alert.status === 'active' && alert.severity === 'critical'
                    ? 'bg-red-50/50 dark:bg-red-900/10'
                    : ''
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1">
                    {getSeverityIcon(alert.severity)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                            {alert.title}
                          </h3>
                          {getSeverityBadge(alert.severity)}
                          {getStatusBadge(alert.status)}
                          {alert.actionRequired && alert.status === 'active' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">
                              Action Required
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
                          {alert.message}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            {getCategoryIcon(alert.category)}
                            <span className="capitalize">{alert.category}</span>
                          </span>
                          <span>{alert.source}</span>
                          <span className="flex items-center gap-1">
                            <ClockIcon className="w-3 h-3" />
                            {formatTimestamp(alert.timestamp)}
                          </span>
                          {alert.relatedEntity && (
                            <span className="text-teal-600 dark:text-teal-400">
                              {alert.relatedEntity}: {alert.relatedEntityId}
                            </span>
                          )}
                        </div>
                        {alert.acknowledgedBy && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Acknowledged by {alert.acknowledgedBy} at {alert.acknowledgedAt}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => setSelectedAlert(alert)}
                          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                          title="View Details"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        {alert.status === 'active' && (
                          <>
                            <button
                              onClick={() => handleAcknowledge(alert.id)}
                              className="p-2 text-yellow-500 hover:text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-lg"
                              title="Acknowledge"
                            >
                              <CheckIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleResolve(alert.id)}
                              className="p-2 text-green-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg"
                              title="Resolve"
                            >
                              <CheckCircleIcon className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {alert.status === 'acknowledged' && (
                          <button
                            onClick={() => handleResolve(alert.id)}
                            className="p-2 text-green-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg"
                            title="Resolve"
                          >
                            <CheckCircleIcon className="w-4 h-4" />
                          </button>
                        )}
                        {(alert.status === 'active' || alert.status === 'acknowledged') && (
                          <button
                            onClick={() => handleDismiss(alert.id)}
                            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                            title="Dismiss"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        )}
                        {(alert.status === 'resolved' || alert.status === 'dismissed') && (
                          <button
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                            title="Delete"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Alert Detail Modal */}
      {selectedAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-start">
                <div className="flex items-start gap-3">
                  {getSeverityIcon(selectedAlert.severity)}
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      {selectedAlert.title}
                    </h2>
                    <p className="text-gray-500 dark:text-gray-400">{selectedAlert.id}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedAlert(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6">
              <div className="flex items-center gap-3 flex-wrap">
                {getSeverityBadge(selectedAlert.severity)}
                {getStatusBadge(selectedAlert.status)}
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 capitalize">
                  {selectedAlert.category}
                </span>
                {selectedAlert.actionRequired && selectedAlert.status === 'active' && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">
                    Action Required
                  </span>
                )}
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Message</h3>
                <p className="text-gray-900 dark:text-white">{selectedAlert.message}</p>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Source</h3>
                  <p className="text-gray-900 dark:text-white">{selectedAlert.source}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Timestamp</h3>
                  <p className="text-gray-900 dark:text-white">{selectedAlert.timestamp}</p>
                </div>
                {selectedAlert.relatedEntity && (
                  <>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Related Entity</h3>
                      <p className="text-gray-900 dark:text-white">{selectedAlert.relatedEntity}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Entity ID</h3>
                      <button
                        onClick={() => navigateToEntity(selectedAlert.relatedEntity, selectedAlert.relatedEntityId)}
                        className="inline-flex items-center gap-1 text-teal-600 dark:text-teal-400 font-medium hover:text-teal-700 dark:hover:text-teal-300 hover:underline transition-colors"
                      >
                        {selectedAlert.relatedEntityId}
                        <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </>
                )}
                {selectedAlert.acknowledgedBy && (
                  <>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Acknowledged By</h3>
                      <p className="text-gray-900 dark:text-white">{selectedAlert.acknowledgedBy}</p>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Acknowledged At</h3>
                      <p className="text-gray-900 dark:text-white">{selectedAlert.acknowledgedAt}</p>
                    </div>
                  </>
                )}
                {selectedAlert.resolvedAt && (
                  <div className="col-span-2">
                    <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Resolved At</h3>
                    <p className="text-gray-900 dark:text-white">{selectedAlert.resolvedAt}</p>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                  <span className={`w-2 h-2 rounded-full ${selectedAlert.autoResolve ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                  Auto-resolve: {selectedAlert.autoResolve ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-between">
              <div className="flex gap-2">
                {selectedAlert.relatedEntityId && (
                  <button
                    onClick={() => navigateToEntity(selectedAlert.relatedEntity, selectedAlert.relatedEntityId)}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-teal-500 text-teal-600 dark:text-teal-400 rounded-lg hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
                  >
                    View {selectedAlert.relatedEntity}
                    <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setSelectedAlert(null)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Close
                </button>
                {selectedAlert.status === 'active' && (
                  <button
                    onClick={() => {
                      handleAcknowledge(selectedAlert.id);
                      setSelectedAlert(null);
                    }}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                  >
                    Acknowledge
                  </button>
                )}
                {(selectedAlert.status === 'active' || selectedAlert.status === 'acknowledged') && (
                  <button
                    onClick={() => {
                      handleResolve(selectedAlert.id);
                      setSelectedAlert(null);
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Resolve
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Configure Alerts Modal */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                  <Cog6ToothIcon className="w-6 h-6 text-teal-600 dark:text-teal-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">Configure Alerts</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Manage notification preferences and alert settings</p>
                </div>
              </div>
              <button
                onClick={() => setShowConfigModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Tab Navigation */}
            <div className="flex border-b border-gray-200 dark:border-gray-700 px-6">
              <button
                onClick={() => setConfigTab('notifications')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  configTab === 'notifications'
                    ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                Notifications
              </button>
              <button
                onClick={() => setConfigTab('categories')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  configTab === 'categories'
                    ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                Categories
              </button>
              <button
                onClick={() => setConfigTab('schedule')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  configTab === 'schedule'
                    ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                Schedule
              </button>
              <button
                onClick={() => setConfigTab('routing')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  configTab === 'routing'
                    ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                Routing Rules
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Notifications Tab */}
              {configTab === 'notifications' && (
                <div className="space-y-6">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Choose how you want to receive alerts. Enable channels and select which severity levels trigger notifications.
                  </p>

                  {/* In-App Notifications */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <ComputerDesktopIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">In-App Notifications</h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Show alerts in the application</p>
                        </div>
                      </div>
                      <button
                        onClick={() => toggleNotificationChannel('inApp')}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          alertConfig.notifications.inApp.enabled ? 'bg-teal-600' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            alertConfig.notifications.inApp.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    {alertConfig.notifications.inApp.enabled && (
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                        {(['critical', 'high', 'medium', 'low', 'info'] as AlertSeverity[]).map((severity) => (
                          <button
                            key={severity}
                            onClick={() => toggleNotificationSeverity('inApp', severity)}
                            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                              alertConfig.notifications.inApp.severities.includes(severity)
                                ? severity === 'critical' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                : severity === 'high' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
                                : severity === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                : severity === 'low' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                : 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
                            }`}
                          >
                            {severity.charAt(0).toUpperCase() + severity.slice(1)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Email Notifications */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <EnvelopeIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">Email Notifications</h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Send alerts to your email address</p>
                        </div>
                      </div>
                      <button
                        onClick={() => toggleNotificationChannel('email')}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          alertConfig.notifications.email.enabled ? 'bg-teal-600' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            alertConfig.notifications.email.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    {alertConfig.notifications.email.enabled && (
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                        {(['critical', 'high', 'medium', 'low', 'info'] as AlertSeverity[]).map((severity) => (
                          <button
                            key={severity}
                            onClick={() => toggleNotificationSeverity('email', severity)}
                            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                              alertConfig.notifications.email.severities.includes(severity)
                                ? severity === 'critical' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                : severity === 'high' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
                                : severity === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                : severity === 'low' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                : 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
                            }`}
                          >
                            {severity.charAt(0).toUpperCase() + severity.slice(1)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* SMS Notifications */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <DevicePhoneMobileIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">SMS Notifications</h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Receive text messages for urgent alerts</p>
                        </div>
                      </div>
                      <button
                        onClick={() => toggleNotificationChannel('sms')}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          alertConfig.notifications.sms.enabled ? 'bg-teal-600' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            alertConfig.notifications.sms.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    {alertConfig.notifications.sms.enabled && (
                      <>
                        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                          {(['critical', 'high', 'medium', 'low', 'info'] as AlertSeverity[]).map((severity) => (
                            <button
                              key={severity}
                              onClick={() => toggleNotificationSeverity('sms', severity)}
                              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                                alertConfig.notifications.sms.severities.includes(severity)
                                  ? severity === 'critical' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                  : severity === 'high' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
                                  : severity === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                  : severity === 'low' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                  : 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                  : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
                              }`}
                            >
                              {severity.charAt(0).toUpperCase() + severity.slice(1)}
                            </button>
                          ))}
                        </div>
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 flex items-center gap-1">
                          <ExclamationTriangleIcon className="w-4 h-4" />
                          SMS charges may apply based on your plan
                        </p>
                      </>
                    )}
                  </div>

                  {/* Push Notifications */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <BellAlertIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">Push Notifications</h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Browser and mobile push notifications</p>
                        </div>
                      </div>
                      <button
                        onClick={() => toggleNotificationChannel('push')}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          alertConfig.notifications.push.enabled ? 'bg-teal-600' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            alertConfig.notifications.push.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    {alertConfig.notifications.push.enabled && (
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                        {(['critical', 'high', 'medium', 'low', 'info'] as AlertSeverity[]).map((severity) => (
                          <button
                            key={severity}
                            onClick={() => toggleNotificationSeverity('push', severity)}
                            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                              alertConfig.notifications.push.severities.includes(severity)
                                ? severity === 'critical' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                : severity === 'high' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
                                : severity === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                : severity === 'low' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                : 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
                            }`}
                          >
                            {severity.charAt(0).toUpperCase() + severity.slice(1)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Categories Tab */}
              {configTab === 'categories' && (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Enable or disable alerts by category. Disabled categories will not generate any notifications.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {([
                      { key: 'shipment', label: 'Shipment Alerts', icon: TruckIcon, description: 'Delivery delays, route deviations' },
                      { key: 'capacity', label: 'Capacity Alerts', icon: BuildingStorefrontIcon, description: 'Warehouse and trailer capacity' },
                      { key: 'delay', label: 'Delay Alerts', icon: ClockIcon, description: 'Weather, traffic, and other delays' },
                      { key: 'inventory', label: 'Inventory Alerts', icon: CubeIcon, description: 'Stock levels and forecasting' },
                      { key: 'system', label: 'System Alerts', icon: ComputerDesktopIcon, description: 'Integration and sync issues' },
                      { key: 'compliance', label: 'Compliance Alerts', icon: CheckCircleIcon, description: 'Regulatory notifications' },
                    ] as const).map(({ key, label, icon: Icon, description }) => (
                      <div
                        key={key}
                        className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                          alertConfig.categories[key]
                            ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                            : 'bg-gray-50 dark:bg-gray-900 border-gray-100 dark:border-gray-800 opacity-60'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className={`w-5 h-5 ${alertConfig.categories[key] ? 'text-teal-600 dark:text-teal-400' : 'text-gray-400'}`} />
                          <div>
                            <h4 className="font-medium text-gray-900 dark:text-white text-sm">{label}</h4>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => toggleCategory(key)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            alertConfig.categories[key] ? 'bg-teal-600' : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              alertConfig.categories[key] ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Schedule Tab */}
              {configTab === 'schedule' && (
                <div className="space-y-6">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Configure quiet hours and automatic alert handling.
                  </p>

                  {/* Quiet Hours */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <ClockIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">Quiet Hours</h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Suppress non-critical notifications during specified hours</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setAlertConfig(prev => ({
                          ...prev,
                          quietHours: { ...prev.quietHours, enabled: !prev.quietHours.enabled }
                        }))}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          alertConfig.quietHours.enabled ? 'bg-teal-600' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            alertConfig.quietHours.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {alertConfig.quietHours.enabled && (
                      <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Time</label>
                            <input
                              type="time"
                              value={alertConfig.quietHours.start}
                              onChange={(e) => setAlertConfig(prev => ({
                                ...prev,
                                quietHours: { ...prev.quietHours, start: e.target.value }
                              }))}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Time</label>
                            <input
                              type="time"
                              value={alertConfig.quietHours.end}
                              onChange={(e) => setAlertConfig(prev => ({
                                ...prev,
                                quietHours: { ...prev.quietHours, end: e.target.value }
                              }))}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                            />
                          </div>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={alertConfig.quietHours.excludeCritical}
                            onChange={(e) => setAlertConfig(prev => ({
                              ...prev,
                              quietHours: { ...prev.quietHours, excludeCritical: e.target.checked }
                            }))}
                            className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            Always send critical alerts (even during quiet hours)
                          </span>
                        </label>
                      </div>
                    )}
                  </div>

                  {/* Auto-Acknowledge */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <CheckCircleIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                        <div>
                          <h4 className="font-medium text-gray-900 dark:text-white">Auto-Acknowledge</h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Automatically acknowledge low-severity alerts after a period</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setAlertConfig(prev => ({
                          ...prev,
                          autoAcknowledge: { ...prev.autoAcknowledge, enabled: !prev.autoAcknowledge.enabled }
                        }))}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          alertConfig.autoAcknowledge.enabled ? 'bg-teal-600' : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            alertConfig.autoAcknowledge.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>

                    {alertConfig.autoAcknowledge.enabled && (
                      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Auto-acknowledge after
                        </label>
                        <select
                          value={alertConfig.autoAcknowledge.afterMinutes}
                          onChange={(e) => setAlertConfig(prev => ({
                            ...prev,
                            autoAcknowledge: { ...prev.autoAcknowledge, afterMinutes: parseInt(e.target.value) }
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                        >
                          <option value={15}>15 minutes</option>
                          <option value={30}>30 minutes</option>
                          <option value={60}>1 hour</option>
                          <option value={120}>2 hours</option>
                          <option value={240}>4 hours</option>
                        </select>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          Only applies to low and info severity alerts
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Routing Rules Tab */}
              {configTab === 'routing' && (
                <AlertNotificationRules />
              )}
            </div>

            {/* Modal Footer - hide for routing tab since it has its own controls */}
            {configTab !== 'routing' && (
              <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowConfigModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveConfig}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                >
                  Save Changes
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Alerts;
